/** Agent model presets (provider + model id). */

export const AGENT_LLM_DEFAULT = {
  provider: 'deepseek',
  model: 'deepseek-flash',
  label: 'Default (DeepSeek V4.1 Flash)',
};

export const AGENT_LLM_PRESETS = [
  { provider: 'grok', model: 'grok-4.6', label: 'Grok 4.6' },
  { provider: 'grok', model: 'grok-4.5', label: 'Grok 4.5' },
  { provider: 'gemini', model: 'gemini-3.8-flash', label: 'Gemini 3.8 Flash' },
  { provider: 'chatgpt', model: 'gpt-6-luna', label: 'GPT-6 Luna' },
  { provider: 'chatgpt', model: 'gpt-5.6-sol', label: 'GPT-5.6 Sol' },
  { provider: 'chatgpt', model: 'gpt-5.6-terra', label: 'GPT-5.6 Terra' },
  { provider: 'deepseek', model: 'deepseek-flash', label: 'DeepSeek V4.1 Flash' },
  { provider: 'zai', model: 'glm-5.3-flash', label: 'GLM 5.3 Flash' },
];

const isAgentLlmDefault = (provider, model) =>
  provider === AGENT_LLM_DEFAULT.provider && model === AGENT_LLM_DEFAULT.model;

/** Map saved model selections to their current replacements. */
const normalizeAgentLlmModel = (provider, model) => {
  const normalizedProvider = String(provider || '').trim();
  const normalizedModel = String(model || '').trim();
  if (
    (normalizedProvider === 'chatgpt' || normalizedProvider === 'openai' || normalizedProvider === 'gpt') &&
    normalizedModel === 'gpt-5.6-luna'
  ) {
    return { provider: 'chatgpt', model: 'gpt-6-luna' };
  }
  if (
    (normalizedProvider === 'chatgpt' || normalizedProvider === 'openai' || normalizedProvider === 'gpt') &&
    normalizedModel === 'gpt-5.5'
  ) {
    return { provider: 'chatgpt', model: 'gpt-5.6-sol' };
  }
  if (
    normalizedProvider === 'gemini' &&
    (normalizedModel === 'gemini-3.6-flash' || normalizedModel === 'gemini-3.7-flash')
  ) {
    return { provider: 'gemini', model: 'gemini-3.8-flash' };
  }
  if (
    normalizedProvider === 'deepseek' &&
    (
      normalizedModel === 'deepseek-v4-flash' ||
      normalizedModel === 'deepseek-v4-pro' ||
      normalizedModel === 'deepseek-v4-flash-vision-exp' ||
      normalizedModel === 'deepseek-v4.1-flash'
    )
  ) {
    return { provider: 'deepseek', model: 'deepseek-flash' };
  }
  return { provider: normalizedProvider, model: normalizedModel };
};

export const buildAgentLlmPresetValue = (provider, model) =>
  `${String(provider || '').trim()}::${String(model || '').trim()}`;

export const parseAgentLlmPresetValue = (value) => {
  const raw = String(value || '').trim();
  if (!raw || raw === 'default') {
    return { provider: null, model: null };
  }
  const [provider, model] = raw.split('::');
  return {
    provider: (provider || '').trim() || null,
    model: (model || '').trim() || null,
  };
};

export const resolveAgentLlmPresetValue = (agent) => {
  const { provider, model } = normalizeAgentLlmModel(
    agent?.llm_provider,
    agent?.llm_model
  );
  if (!provider && !model) return 'default';
  if (isAgentLlmDefault(provider, model)) return 'default';
  const match = AGENT_LLM_PRESETS.find(
    (preset) => preset.provider === provider && preset.model === model
  );
  if (match) return buildAgentLlmPresetValue(match.provider, match.model);
  if (provider && model) return buildAgentLlmPresetValue(provider, model);
  return 'default';
};

export const getAgentLlmDisplayLabel = (agent) => {
  const { provider, model } = normalizeAgentLlmModel(
    agent?.llm_provider,
    agent?.llm_model
  );
  if (!provider && !model) return AGENT_LLM_DEFAULT.label;
  if (isAgentLlmDefault(provider, model)) return AGENT_LLM_DEFAULT.label;
  const match = AGENT_LLM_PRESETS.find(
    (preset) => preset.provider === provider && preset.model === model
  );
  if (match) return match.label;
  return model || provider || AGENT_LLM_DEFAULT.label;
};
